import React from 'react';
import { useAuth } from '../auth';
import { useRouter } from './index';
import env from '../env';

/**
 * SecurePdfViewer component that fetches and displays PDFs with authentication
 * @param {Object} props
 * @param {string} props.href - PDF URL
 * @param {string} props.children - Link text
 * @param {string} props.className - CSS classes
 * @param {Object} rest - Other props
 */
export function SecurePdfViewer({ href, children, className = '', pageUrl = null, ...rest }) {
  const auth = useAuth();
  const { pathname } = useRouter();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const currentPageUrl = pageUrl || pathname;

  // Check if this is a PDF link
  const isPdfLink = href && href.toLowerCase().endsWith('.pdf');
  
  // If not a PDF link, render as normal link
  if (!isPdfLink) {
    return (
      <a href={href} className={className} {...rest}>
        {children}
      </a>
    );
  }

  const handleClick = async (e) => {
    e.preventDefault();
    
    if (!href) {
      setError('PDF link is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('Opening PDF viewer for:', href);
      
      // Get authentication token
      let token = null;
      if (auth.user && typeof auth.user.getIdToken === 'function') {
        token = await auth.user.getIdToken();
      } else if (auth.currentFirebaseUser) {
        const firebaseUser = await auth.currentFirebaseUser();
        token = firebaseUser ? await firebaseUser.getIdToken() : null;
      }
      
      if (!token) {
        console.error('No authentication token available');
        setError('Authentication required. Please log in.');
        return;
      }

      // Convert href to filePath for the secure API
      let filePath = href.replace(/^\/media\//, '');
      filePath = filePath.replace(/^\\\//, '').replace(/^\//, '');
      
      // Use the secure API endpoint for viewing (inline)
      const secureUrl = `${env.apiBase}/api/securepdf/getpdf?filePath=${encodeURIComponent(filePath)}&pageUrl=${encodeURIComponent(currentPageUrl)}&download=false`;
      console.log('Requesting PDF from:', secureUrl);

      // Fetch the PDF with authentication using secure endpoint
      const response = await fetch(secureUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });

      console.log('PDF response received:', response.status, response.statusText);

      if (!response.ok) {
        console.error('PDF loading failed with status:', response.status);
        if (response.status === 401) {
          throw new Error('Access denied. You do not have permission to view this PDF.');
        } else if (response.status === 404) {
          throw new Error('PDF file not found.');
        } else {
          throw new Error(`Failed to load PDF: ${response.statusText}`);
        }
      }

      // Get the PDF blob and open it
      console.log('Converting response to blob for viewing...');
      const blob = await response.blob();
      console.log('Blob created for viewing, size:', blob.size, 'bytes');
      const url = window.URL.createObjectURL(blob);
      
      // Open PDF in new tab for viewing (users can download from browser's PDF viewer)
      console.log('Opening PDF in new tab for viewing');
      const newWindow = window.open(url, '_blank');
      
      if (!newWindow) {
        setError('Popup blocked. Please allow popups for this site to view PDFs.');
        window.URL.revokeObjectURL(url);
        return;
      }
      
      // Clean up the object URL after a longer delay to ensure PDF loads properly
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        console.log('PDF viewer URL cleaned up');
      }, 10000); // 10 seconds instead of 1 second

    } catch (err) {
      console.error('PDF access error:', err);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to load PDF';
      
      if (err.name === 'AbortError') {
        errorMessage = 'PDF loading timed out. Please check your connection and try again.';
      } else if (err.name === 'TypeError' && err.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (err.message.includes('Authentication required')) {
        errorMessage = 'Please log in to view this file.';
      } else if (err.message.includes('Access denied')) {
        errorMessage = 'You do not have permission to view this file.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="secure-pdf-viewer">
      <a
        href="#"
        onClick={handleClick}
        className={`${className} ${isLoading ? 'loading' : ''}`}
        {...rest}
      >
        {isLoading ? 'Loading PDF...' : children}
      </a>
      
      {error && (
        <div className="pdf-error" style={{ color: 'red', fontSize: '0.9em', marginTop: '5px' }}>
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * SecurePdfDownload component for downloading PDFs
 */
export function SecurePdfDownload({ href, children, className = '', pageUrl = null, ...rest }) {
  const auth = useAuth();
  const { pathname } = useRouter();
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const currentPageUrl = pageUrl || pathname;
  const isPdfLink = href && href.toLowerCase().endsWith('.pdf');
  
  if (!isPdfLink) {
    return (
      <a href={href} className={className} {...rest}>
        {children}
      </a>
    );
  }

  const handleDownload = async (e) => {
    e.preventDefault();
    
    if (!href) {
      setError('PDF link is required');
      return;
    }

    setIsDownloading(true);
    setError(null);

    try {
      console.log('Starting PDF download for:', href);
      
      let token = null;
      if (auth.user && typeof auth.user.getIdToken === 'function') {
        token = await auth.user.getIdToken();
      } else if (auth.currentFirebaseUser) {
        const firebaseUser = await auth.currentFirebaseUser();
        token = firebaseUser ? await firebaseUser.getIdToken() : null;
      }
      
      if (!token) {
        console.error('No authentication token available');
        setError('Authentication required. Please log in.');
        return;
      }

      // Convert href to filePath for the secure API
      let filePath = href.replace(/^\/media\//, '');
      filePath = filePath.replace(/^\\\//, '').replace(/^\//, '');
      const fileName = filePath.split('/').pop();
      
      // Use the secure API endpoint for downloading
      const secureUrl = `${env.apiBase}/api/securepdf/getpdf?filePath=${encodeURIComponent(filePath)}&pageUrl=${encodeURIComponent(currentPageUrl)}&download=true`;
      console.log('Requesting PDF from:', secureUrl);

      // Create timeout controller for better error handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(secureUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log('PDF response received:', response.status, response.statusText);

      if (!response.ok) {
        console.error('PDF download failed with status:', response.status);
        if (response.status === 401) {
          throw new Error('Access denied. You do not have permission to download this PDF.');
        } else if (response.status === 404) {
          throw new Error('PDF file not found.');
        } else {
          throw new Error(`Download failed: ${response.statusText}`);
        }
      }

      // Download the PDF
      console.log('Converting response to blob...');
      const blob = await response.blob();
      console.log('Blob created, size:', blob.size, 'bytes');
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      console.log('Triggering download for file:', fileName);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        console.log('PDF download completed and URL revoked');
      }, 1000);

    } catch (err) {
      console.error('PDF download error:', err);
      
      // Provide more specific error messages
      let errorMessage = 'Download failed';
      
      if (err.name === 'AbortError') {
        errorMessage = 'Download timed out. Please check your connection and try again.';
      } else if (err.name === 'TypeError' && err.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (err.message.includes('Authentication required')) {
        errorMessage = 'Please log in to download this file.';
      } else if (err.message.includes('Access denied')) {
        errorMessage = 'You do not have permission to download this file.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="secure-pdf-download">
      <a
        href="#"
        onClick={handleDownload}
        className={`${className} ${isDownloading ? 'downloading' : ''}`}
        {...rest}
      >
        {isDownloading ? 'Downloading...' : children}
      </a>
      
      {error && (
        <div className="pdf-error" style={{ color: 'red', fontSize: '0.9em', marginTop: '5px' }}>
          {error}
        </div>
      )}
    </div>
  );
} 